import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminFormSecondaryAction,
  adminFormDetailSelectClasses,
  adminFormDetailInputClasses,
  adminFormTwoColGridClass,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_FORM_FOOTER_CLASS,
} from '../shared/adminUx';
import {
  canManagePermissionsAccess,
  canViewPermissionsAccess,
  getActorDisplayName,
} from '../../../data/teamConfig';
import {
  applyModuleActionToggle,
  CONFIGURATION_STATUS,
  DEFAULT_DATA_ACCESS_SCOPE_OPTIONS,
  emptyModulePermission,
  formatPermissionDateDisplay,
  formatPermissionDateOnly,
  getDefaultDataAccessScopeLabel,
  getModuleLabel,
  MODULE_ACTION_KEYS,
  MODULE_ACTION_LABELS,
  MODULE_OPTIONS,
  moduleHasAnyPermission,
  normalizeModulePermissions,
  PERMISSIONS_ACCESS_ROUTE,
} from '../../../data/permissionsConfig';
import {
  applyPermissionConfigSave,
  buildSamplePermissionConfigs,
  collectPermissionConfigSaveErrors,
  formatExtendedModuleActionsSummary,
  getDepartmentsForTeamType,
  getJobRolesForDepartment,
  isModuleAssignableToOrdinaryMember,
  TEAM_TYPE_OPTIONS,
} from '../../../data/permissionsAccessMatrix';

const FORM_ACTION_KEYS = MODULE_ACTION_KEYS.filter((k) => k !== 'archive');

const PermissionConfigurationDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId: configIdParam } = useParams();
  const { auth } = useAuth();
  const canView = canViewPermissionsAccess(auth);
  const canManage = canManagePermissionsAccess(auth);
  const actorName = getActorDisplayName(auth);

  const isEdit = /\/edit$/i.test(location.pathname);
  const configId = decodeURIComponent(configIdParam || '');

  const [configs, setConfigs] = useState(
    () => location.state?.configs || buildSamplePermissionConfigs(),
  );
  const selected = useMemo(
    () =>
      configs.find((c) => c.id === configId) ||
      (location.state?.config?.id === configId ? location.state.config : null),
    [configs, configId, location.state],
  );

  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!selected) return;
    const next = {
      permissionPresetName: selected.permissionPresetName || '',
      teamType: selected.teamType || '',
      department: selected.department || '',
      jobRole: selected.jobRole || '',
      defaultAccessLabel: selected.defaultAccessLabel || '',
      dashboard: selected.dashboard || '',
      customisableModules: [...(selected.customisableModules || [])],
      modulePermissions: normalizeModulePermissions(selected.modulePermissions),
      status: selected.status || CONFIGURATION_STATUS.ACTIVE,
    };
    setForm(next);
    setBaseline(next);
    setErrors({});
    setSaveError('');
  }, [selected]);

  const departmentOptions = useMemo(
    () => getDepartmentsForTeamType(form?.teamType),
    [form?.teamType],
  );
  const jobRoleOptions = useMemo(
    () => getJobRolesForDepartment(form?.teamType, form?.department),
    [form?.teamType, form?.department],
  );

  const identityLocked = Boolean(selected?.lockedIdentity);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleTeamTypeChange = (teamType) => {
    if (identityLocked) return;
    setForm((prev) => ({
      ...prev,
      teamType,
      department: '',
      jobRole: '',
    }));
  };

  const handleDepartmentChange = (department) => {
    if (identityLocked) return;
    setForm((prev) => ({
      ...prev,
      department,
      jobRole: '',
    }));
  };

  const toggleModuleSelected = (moduleValue, checked) => {
    setForm((prev) => {
      let rows = normalizeModulePermissions(prev.modulePermissions);
      if (checked) {
        if (!rows.some((r) => r.module === moduleValue)) {
          rows = [...rows, { ...emptyModulePermission(moduleValue), view: true }];
        }
      } else {
        rows = rows.filter((r) => r.module !== moduleValue);
      }
      return { ...prev, modulePermissions: rows };
    });
  };

  const toggleAction = (moduleValue, actionKey, checked) => {
    setForm((prev) => {
      let rows = normalizeModulePermissions(prev.modulePermissions);
      if (!rows.some((r) => r.module === moduleValue)) {
        rows = [...rows, emptyModulePermission(moduleValue)];
      }
      return {
        ...prev,
        modulePermissions: rows.map((row) =>
          row.module === moduleValue ? applyModuleActionToggle(row, actionKey, checked) : row,
        ),
      };
    });
  };

  const setScope = (moduleValue, scope) => {
    setForm((prev) => {
      let rows = normalizeModulePermissions(prev.modulePermissions);
      if (!rows.some((r) => r.module === moduleValue)) {
        rows = [...rows, emptyModulePermission(moduleValue)];
      }
      return {
        ...prev,
        modulePermissions: rows.map((row) =>
          row.module === moduleValue ? { ...row, dataAccessScope: scope } : row,
        ),
      };
    });
  };

  const handleSave = () => {
    if (!canManage || !selected || !form) return;
    const nextErrors = collectPermissionConfigSaveErrors(form, {
      configs,
      excludeId: selected.id,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setSaveError('Unable to save. Review the form and try again.');
      return;
    }
    const updated = applyPermissionConfigSave(selected, form, { actorName });
    const nextConfigs = configs.map((c) => (c.id === selected.id ? updated : c));
    setConfigs(nextConfigs);
    navigate(PERMISSIONS_ACCESS_ROUTE, {
      state: { configs: nextConfigs, feedback: 'Permission Configuration saved.' },
    });
  };

  if (!canView) {
    return (
      <AdminLayout>
        <AdminPageTitle>Permissions &amp; Access</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to access Permissions &amp; Access.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (!selected || !form) {
    return (
      <AdminLayout>
        <AdminSupportingText className="text-mediumgray">
          Permission Configuration not found.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isEdit && !canManage) {
    return (
      <AdminLayout>
        <AdminPageTitle>Edit Permission Configuration</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          Only the Platform Owner can edit Permission Configurations.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const granted = normalizeModulePermissions(form.modulePermissions).filter(moduleHasAnyPermission);
  const pageTitle = isEdit ? 'Edit Permission Configuration' : 'Permission Configuration Detail';
  const selectableModules = MODULE_OPTIONS.filter((m) =>
    isModuleAssignableToOrdinaryMember(m.value),
  );

  const renderReadOnly = (label, value) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    </div>
  );

  return (
    <AdminLayout>
      <div
        className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 w-full min-w-0 ${ADMIN_PAGE_TITLE_CLASS}`}
      >
        <AdminPageTitle className="!mb-0">{pageTitle}</AdminPageTitle>
      </div>

      <div className="space-y-4">
        <DashboardMainContainer className={isEdit ? ADMIN_FORM_PANEL_CLASS : '!bg-white w-full'}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-2">Identity &amp; Metadata</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {renderReadOnly('Permission ID', selected.id)}
              {renderReadOnly('Date Added', formatPermissionDateOnly(selected.dateAdded))}
              {renderReadOnly('Last Updated', formatPermissionDateDisplay(selected.lastUpdated))}
              {isEdit ? (
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>
                    Permission Preset name <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <input
                    type="text"
                    value={form.permissionPresetName}
                    onChange={(e) => updateField('permissionPresetName', e.target.value)}
                    className={adminFormDetailInputClasses(false, Boolean(errors.permissionPresetName))}
                  />
                  {errors.permissionPresetName ? (
                    <AdminFormFieldError>{errors.permissionPresetName}</AdminFormFieldError>
                  ) : null}
                </div>
              ) : (
                renderReadOnly('Permission Preset name', form.permissionPresetName)
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={isEdit ? ADMIN_FORM_PANEL_CLASS : '!bg-white w-full'}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-2">Role Matrix</AdminPanelTitle>
            <div className={adminFormTwoColGridClass}>
              {isEdit && !identityLocked ? (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Team Type <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <select
                      value={form.teamType}
                      onChange={(e) => handleTeamTypeChange(e.target.value)}
                      className={adminFormDetailSelectClasses(false, Boolean(errors.teamType))}
                    >
                      <option value="">Select Team Type</option>
                      {TEAM_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {errors.teamType ? <AdminFormFieldError>{errors.teamType}</AdminFormFieldError> : null}
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Department <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <select
                      value={form.department}
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      className={adminFormDetailSelectClasses(false, Boolean(errors.department))}
                    >
                      <option value="">Select Department</option>
                      {departmentOptions.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    {errors.department ? (
                      <AdminFormFieldError>{errors.department}</AdminFormFieldError>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Job Role <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <select
                      value={form.jobRole}
                      onChange={(e) => updateField('jobRole', e.target.value)}
                      className={adminFormDetailSelectClasses(false, Boolean(errors.jobRole))}
                    >
                      <option value="">Select Job Role</option>
                      {jobRoleOptions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    {errors.jobRole ? <AdminFormFieldError>{errors.jobRole}</AdminFormFieldError> : null}
                  </div>
                </>
              ) : (
                <>
                  {renderReadOnly('Team Type', form.teamType)}
                  {renderReadOnly('Department', form.department)}
                  {renderReadOnly('Job Role', form.jobRole)}
                </>
              )}
              {isEdit ? (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Default Access</AdminDetailFieldLabel>
                    <input
                      type="text"
                      value={form.defaultAccessLabel}
                      onChange={(e) => updateField('defaultAccessLabel', e.target.value)}
                      className={adminFormDetailInputClasses(false)}
                    />
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Team Dashboard name</AdminDetailFieldLabel>
                    <input
                      type="text"
                      value={form.dashboard}
                      onChange={(e) => updateField('dashboard', e.target.value)}
                      className={adminFormDetailInputClasses(false)}
                    />
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Configuration Status <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <select
                      value={form.status}
                      onChange={(e) => updateField('status', e.target.value)}
                      className={adminFormDetailSelectClasses(false, Boolean(errors.status))}
                    >
                      <option value={CONFIGURATION_STATUS.ACTIVE}>Active</option>
                      <option value={CONFIGURATION_STATUS.INACTIVE}>Inactive</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {renderReadOnly('Default Access', form.defaultAccessLabel)}
                  {renderReadOnly('Team Dashboard name', form.dashboard)}
                  {renderReadOnly('Configuration Status', form.status)}
                </>
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={isEdit ? ADMIN_FORM_PANEL_CLASS : '!bg-white w-full'}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-2">Modules, Permissions &amp; Scope</AdminPanelTitle>
            {errors.modulePermissions ? (
              <AdminFormFieldError>{errors.modulePermissions}</AdminFormFieldError>
            ) : null}
            {isEdit ? (
              <div className="space-y-3">
                {selectableModules.map((module) => {
                  const row =
                    normalizeModulePermissions(form.modulePermissions).find(
                      (r) => r.module === module.value,
                    ) || emptyModulePermission(module.value);
                  const selectedModule = moduleHasAnyPermission(row);
                  return (
                    <div
                      key={module.value}
                      className={`rounded-xl border p-3 space-y-3 ${
                        selectedModule
                          ? 'border-primaryblue bg-pastalBlueWhite'
                          : 'border-lightgray bg-white'
                      }`}
                    >
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedModule}
                          onChange={(e) => toggleModuleSelected(module.value, e.target.checked)}
                          className="h-4 w-4 rounded border-lightgray text-primaryblue focus:ring-primaryblue"
                        />
                        <AdminDetailFieldValue as="span" emphasis className="!mb-0">
                          {module.label}
                        </AdminDetailFieldValue>
                      </label>
                      {selectedModule ? (
                        <>
                          <div className="flex flex-wrap gap-3">
                            {FORM_ACTION_KEYS.map((actionKey) => (
                              <label
                                key={actionKey}
                                className="inline-flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(row[actionKey])}
                                  onChange={(e) =>
                                    toggleAction(module.value, actionKey, e.target.checked)
                                  }
                                  className="h-4 w-4 rounded border-lightgray text-primaryblue focus:ring-primaryblue"
                                />
                                <AdminSupportingText as="span" className="text-offblack">
                                  {MODULE_ACTION_LABELS[actionKey]}
                                </AdminSupportingText>
                              </label>
                            ))}
                          </div>
                          <div className="space-y-1 max-w-sm">
                            <AdminDetailFieldLabel>Data-access Scope</AdminDetailFieldLabel>
                            <select
                              value={row.dataAccessScope || ''}
                              onChange={(e) => setScope(module.value, e.target.value)}
                              className={adminFormDetailSelectClasses(false)}
                            >
                              {DEFAULT_DATA_ACCESS_SCOPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <ul className="divide-y divide-lightgray border-y border-lightgray">
                {granted.map((row) => (
                  <li key={row.module} className="py-2.5">
                    <AdminDetailFieldValue className="!mb-0">
                      {getModuleLabel(row.module)}
                    </AdminDetailFieldValue>
                    <AdminSupportingText as="p" className="text-mediumgray mt-0.5">
                      {formatExtendedModuleActionsSummary(row)} —{' '}
                      {getDefaultDataAccessScopeLabel(row.dataAccessScope)}
                    </AdminSupportingText>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DashboardMainContainer>

        {isEdit ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-3">
              <AdminPanelTitle className="text-offblack mb-2">Review proposed changes</AdminPanelTitle>
              <AdminSupportingText className="text-offblack">
                Current Status: {baseline?.status || '—'} → Proposed Status: {form.status}
              </AdminSupportingText>
              <AdminSupportingText className="text-offblack">
                Modules granted: {granted.map((r) => getModuleLabel(r.module)).join(' · ') || '—'}
              </AdminSupportingText>
              {saveError ? <AdminFormFieldError>{saveError}</AdminFormFieldError> : null}
            </div>
          </DashboardMainContainer>
        ) : null}
      </div>

      {isEdit ? (
        <div className={ADMIN_FORM_FOOTER_CLASS}>
          <div className="flex flex-wrap gap-3 items-center justify-end">
            <AdminFormSecondaryAction
              type="button"
              onClick={() =>
                navigate(`${PERMISSIONS_ACCESS_ROUTE}/config/${encodeURIComponent(selected.id)}`, {
                  state: { configs, config: selected },
                })
              }
            >
              Cancel
            </AdminFormSecondaryAction>
            <AdminHeaderAction type="button" onClick={handleSave}>
              Save
            </AdminHeaderAction>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
};

export default PermissionConfigurationDetail;
